import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import {
  Button,
  Flex,
  FlexColumn,
  FlexSpace,
  Breadcrumbs,
} from '@elementary/common-ui/components';
import { CircularProgress, Skeleton } from '@mui/material';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  DWH_CREATE,
  TEAMS_BOT_CONNECT,
} from '@elementary/common-ui/constants/urlParams';
import NoEnvPage from 'shared/components/NoEnvPage';
import useEnvironments from 'shared/hooks/useEnvironments';
import useIntegrations from 'shared/hooks/useIntegrations';
import {
  useEnvironmentsSetup,
  EnvironmentMutationInput,
} from 'shared/hooks/integrations';
import { fontSize, radius, space } from '@elementary/common-ui/style/utils';
import { parseEnvironmentError } from '../../utils';
import EnvironmentDrawer from './EnvironmentDrawer/EnvironmentDrawer';
import IntegrationAccordion from './IntegrationAccordion/IntegrationAccordion';

const Wrapper = styled(FlexColumn)`
  width: 100%;
  height: 100%;
  gap: ${space('xxl')};
  padding-bottom: ${space('xxl')};
`;

const CenteringWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const LoadingSkeleton = styled(Skeleton)`
  border-radius: ${radius('lg')};
  background-color: ${({ theme }) => theme.palette.surface.muted};
  color: ${({ theme }) => theme.palette.content.primary};
  font-size: ${fontSize('sm')};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 94px;
`;

const Environments = () => {
  const [lastAddedEnvName, setLastAddedEnvName] = useState<string>();
  const [showNewEnvLoading, setShowNewEnvLoading] = useState(false);
  const curEnvRef = useRef<any>();
  const envsContainerRef = useRef<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const showDwhForm = searchParams.get(DWH_CREATE) === 'true';
  const setShowDwhForm = useCallback(
    (open: boolean) => {
      if (open) {
        setSearchParams((prev) => {
          prev.set(DWH_CREATE, 'true');
          return prev;
        });
      } else {
        setSearchParams((prev) => {
          prev.delete(DWH_CREATE);
          return prev;
        });
      }
    },
    [setSearchParams],
  );

  const {
    data: environmentsData,
    isLoading: environmentsLoading,
    selectedEnvId,
  } = useEnvironments({
    onEnvironmentsLoaded: () => setShowNewEnvLoading(false),
  });

  const { integrations, isLoading: integrationsLoading } = useIntegrations();

  const { environmentMutation } = useEnvironmentsSetup({
    onUpdateSuccess: () => {
      setShowDwhForm(false);
      setShowNewEnvLoading(true);
    },
  });

  const editableEnvironments = useMemo(
    () =>
      environmentsData?.filter((env) =>
        env.permissions?.includes('EDIT_CONFIG'),
      ),
    [environmentsData],
  );

  const environmentError = useMemo(
    () => parseEnvironmentError(environmentMutation.error),
    [environmentMutation.error],
  );

  const focusedEnvElementIndex = useMemo(
    () =>
      editableEnvironments?.findIndex((item) => {
        if (lastAddedEnvName) {
          return item.name === lastAddedEnvName;
        }
        return item.id === selectedEnvId;
      }),
    [editableEnvironments, lastAddedEnvName, selectedEnvId],
  );

  const isTeamsBotConnectFromUrl = searchParams.get(TEAMS_BOT_CONNECT) === '1';
  const teamsBotConnectHandlerIndex = useMemo(() => {
    if (!isTeamsBotConnectFromUrl || !editableEnvironments?.length) {
      return -1;
    }
    if (focusedEnvElementIndex !== undefined && focusedEnvElementIndex >= 0) {
      return focusedEnvElementIndex;
    }
    return 0;
  }, [
    editableEnvironments?.length,
    focusedEnvElementIndex,
    isTeamsBotConnectFromUrl,
  ]);

  useEffect(() => {
    if (
      curEnvRef?.current &&
      envsContainerRef?.current &&
      focusedEnvElementIndex !== undefined
    ) {
      envsContainerRef.current.scrollTo({
        top: focusedEnvElementIndex * 110,
        behavior: 'smooth',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curEnvRef?.current, focusedEnvElementIndex]);

  const submitNewEnv = (data: EnvironmentMutationInput) => {
    environmentMutation.mutate(data);
    setLastAddedEnvName(data.environmentName);
  };

  useEffect(() => {
    setLastAddedEnvName(undefined);
  }, [selectedEnvId]);

  if (environmentsLoading) {
    return (
      <CenteringWrapper>
        <CircularProgress />
      </CenteringWrapper>
    );
  }

  return (
    <Wrapper>
      <EnvironmentDrawer
        open={showDwhForm}
        onClose={() => setShowDwhForm(false)}
        onSubmit={submitNewEnv}
        loading={environmentMutation.isLoading}
        error={environmentError}
      />

      {editableEnvironments?.length === 0 ? (
        <NoEnvPage />
      ) : (
        <>
          <FlexSpace>
            <Breadcrumbs
              items={['Configuration', 'Environments']}
              showTreeButton={false}
            />

            <Button
              variant="outlined"
              startIcon={<PlusIcon />}
              onClick={() => setShowDwhForm(true)}
            >
              Create environment
            </Button>
          </FlexSpace>

          <FlexColumn
            ref={envsContainerRef}
            sx={{ overflow: 'auto', gap: space('xxl') }}
          >
            {showNewEnvLoading && (
              <LoadingSkeleton
                variant="rectangular"
                width="100%"
                height={94}
                animation="wave"
              >
                Loading new environment...
              </LoadingSkeleton>
            )}
            {editableEnvironments?.map((env, i) => (
              <IntegrationAccordion
                ref={focusedEnvElementIndex === i ? curEnvRef : undefined}
                key={env.id}
                environmentData={env}
                isFocused={focusedEnvElementIndex === i}
                envIntegrations={integrations?.[env.id]}
                isIntegrationsLoading={integrationsLoading}
                shouldHandleTeamsBotConnect={teamsBotConnectHandlerIndex === i}
              />
            ))}
          </FlexColumn>
        </>
      )}
    </Wrapper>
  );
};

export default Environments;
